import { state, loadFromLocalStorage, saveToLocalStorage, getActiveSettings } from '../state.js';
import { escapeHtml, playerBirthYear } from '../utils/formatting.js';
import {
    sortPublicTournaments,
    matchesFormat,
    matchesRatingType,
    matchesSpecificDate,
    matchesPeriod,
    matchesCity,
    extractCity
} from '../utils/tournament-sort.js';
import { buildTournamentPath, parseDeepLink, parseViewHash, TOURNAMENT_VIEWS } from '../utils/deep-link.js';
import { updateUIState, renderTournamentsPage, collapseAllFolders, renderDashboardGroupsBar } from '../app.js';
import { renderPlayerList, getPlayerDisplayRating } from './players.js';
import { renderCurrentRound, saveEditedPairings, saveRegisterResults } from './pairings.js';
import { renderStandings } from './standings.js';
import { renderDatabasesPage } from './player-db-page.js';
import { renderOrganizersPage } from './organizers-page.js';
import { publicOrganizerOptions, matchesOrganizer } from '../utils/organizer-logic.js';
import { renderMyPlayTournaments } from './player-tournaments.js';
import { syncTopHeader } from './top-header.js';
import { initNavOrder, applyNavOrder, hasCustomNavOrder, resetNavOrder } from './nav-order.js';
import { initNavGroup, setNavGroupFolded, syncNavGroup } from './nav-group.js';
import { refreshNsfCheck } from '../services/nsf-membership.js';

// DOM Elements for Views
const views = document.querySelectorAll('.view');

// Navigation Bars
const arbiterNav = document.getElementById('arbiter-nav');
const allNavItems = document.querySelectorAll('.nav-item[data-target], .logo-area[data-target]');

// Navigation Links
const navDashboard = document.querySelector('.nav-item[data-target="dashboard"]');
const navMyTournaments = document.querySelector('.nav-item[data-target="tournaments"]');
const navMyPlay = document.getElementById('nav-my-play');
const navDatabases = document.getElementById('nav-databases');
const navOrganizers = document.getElementById('nav-organizers');
const navOngoing = document.getElementById('nav-ongoing');
const navUpcoming = document.getElementById('nav-upcoming');
const navFinished = document.getElementById('nav-finished');
const navPlayers = document.getElementById('nav-players');
const navPairings = document.getElementById('nav-pairings');
const navStandings = document.getElementById('nav-standings');
const navLive = document.getElementById('nav-live');
const navSettings = document.querySelector('.nav-item[data-target="settings"]');
const navGuide = document.getElementById('nav-guide');
// The ? in the top bar: the visual tour (js/ui/tour.js), for any signed-in account.
const btnHelpTour = document.getElementById('btn-help-tour');

// The two Live entry points do NOT follow the same rule, and that is deliberate.
//
// The spectator tab (#tab-live, top panel) is for watching, so it appears only
// once the tournament is actually broadcasting — there is nothing behind it
// otherwise.
//
// The arbiter's sidebar item (#nav-live) is where broadcasting is switched ON, so
// hiding it until the broadcast is enabled would hide the switch inside the room
// it unlocks. It is shown to any signed-in arbiter with a tournament open.
//
// Kept together because the two are toggled from different entry points
// (updateNavigationVisibility for the sidebar, updateSpectatorUI for the top
// tabs) and the reason they differ has to be in one place.
function broadcastEnabled() {
    const t = state.activeTournament;
    return !!(t && t.broadcast && t.broadcast.enabled);
}

function syncLiveTabVisibility() {
    const tab = document.getElementById('tab-live');
    if (tab) tab.style.display = broadcastEnabled() ? '' : 'none';
}

// Admin Elements to toggle in Spectator Mode
const btnCreateTourneyMain = document.getElementById('btn-create-tournament-main');
const btnCloseTournament = document.getElementById('btn-close-tournament');
const btnOpenTourneyMain = document.getElementById('btn-open-tournament-main');
const btnAddPlayer = document.getElementById('btn-add-player');
const btnGenerateRound = document.getElementById('btn-generate-round');
const btnRegisterResults = document.getElementById('btn-register-results');
const btnUndoRound = document.getElementById('btn-undo-round');
const btnEditTournament = document.getElementById('btn-edit-tournament');
const btnCreateTourneyPage = document.getElementById('btn-create-tournament-page');
const btnAssignSNR = document.getElementById('btn-assign-snr');
const btnToggleAllStatus = document.getElementById('btn-toggle-all-status');

function isPlayerFormFilled() {
    const fields = [
        'player-db-search',
        'player-start-no',
        'player-class',
        'player-given-name',
        'player-last-name',
        'player-club',
        'player-fed',
        'player-birthyear',
        'player-fide-id',
        'player-nsf-id',
        'player-rating',
        'player-k-factor'
    ];
    for (const id of fields) {
        const el = document.getElementById(id);
        if (el && el.value.trim() !== '') {
            return true;
        }
    }
    const titleSelect = document.getElementById('player-title');
    if (titleSelect && titleSelect.value !== '') {
        return true;
    }
    return false;
}

function isTournamentFormEdited() {
    const textFields = {
        'tourney-name': '',
        'tourney-location': '',
        'tourney-start': '',
        'tourney-start-time': '09:00',
        'tourney-end': '',
        'tourney-end-time': '18:00',
        'tourney-format': 'swiss',
        'tourney-type': 'standard',
        'tourney-rounds': '7',
        'tourney-seed-color': 'white',
        'tourney-points': '1-0.5-0',
        'tourney-welcome': '',
        'tourney-arbiter-name': '',
        'tourney-arbiter-email': '',
        'tourney-arbiter-phone': ''
    };
    for (const [id, defVal] of Object.entries(textFields)) {
        const el = document.getElementById(id);
        if (el && el.value.trim() !== defVal) {
            return true;
        }
    }
    const publicCheckbox = document.getElementById('tourney-public');
    if (publicCheckbox && publicCheckbox.checked) {
        return true;
    }
    return false;
}

export function initNavigation() {
    initNavOrder();
    initNavGroup();
    document.getElementById('btn-reset-nav-order')?.addEventListener('click', resetNavOrder);

    // Generated markup asks for navigation declaratively — data-show-view="ongoing"
    // on a button, data-watch-tournament="<id>" on a tournament card — and this one
    // listener carries it out. Inline onclick handlers would need 'unsafe-inline'
    // in the Content-Security-Policy, and an id spliced into one is a JS string
    // that HTML escaping alone does not make safe.
    document.addEventListener('click', (e) => {
        const card = e.target.closest('[data-watch-tournament]');
        if (card) {
            window.app.watchTournament(card.dataset.watchTournament);
            return;
        }
        const link = e.target.closest('[data-show-view]');
        if (link) window.app.showView(link.dataset.showView);
    });

    // Track where a mouse press starts so that dragging from inside a modal
    // out onto the overlay (which fires a "click" on the overlay) is not
    // mistaken for an intentional click outside the popup.
    let mouseDownTarget = null;
    document.addEventListener('mousedown', (e) => {
        mouseDownTarget = e.target;
    });

    document.addEventListener('click', async (e) => {
        // 1. Close Modal Buttons
        const closeBtn = e.target.closest('.btn-close-modal');
        if (closeBtn) {
            e.preventDefault();
            const modal = closeBtn.closest('.modal-overlay');
            if (modal) {
                if (modal.id === 'add-player-modal') {
                    const isEditing = !!state.editingPlayerId;
                    if (!isEditing && isPlayerFormFilled()) {
                        const confirmCancel = await window.Dialog.confirm(
                            tr('Are you sure you want to cancel player registration? Your changes will be lost.'),
                            tr('Cancel Registration'),
                            tr('Yes, Cancel'),
                            true,
                            tr('Keep Editing')
                        );
                        if (!confirmCancel) {
                            return; // Keep modal open
                        }
                    }
                    // Defer form reset until after the close transition to avoid a flash of defaults
                    state.editingPlayerId = null;
                    const addPlayerForm = document.getElementById('add-player-form');
                    setTimeout(() => {
                        if (addPlayerForm && !modal.classList.contains('active')) addPlayerForm.reset();
                    }, 300);
                } else if (modal.id === 'create-tournament-modal') {
                    const isEdit = modal.dataset.mode === 'edit';
                    if (!isEdit && isTournamentFormEdited()) {
                        const confirmCancel = await window.Dialog.confirm(
                            tr('Are you sure you want to cancel tournament creation? Your changes will be lost.'),
                            tr('Cancel Tournament'),
                            tr('Yes, Cancel'),
                            true,
                            tr('Keep Editing')
                        );
                        if (!confirmCancel) {
                            return; // Keep modal open
                        }
                    }
                    if (!isEdit) {
                        // Creation cancelled: discard any staged cooperation invites so
                        // they are never sent and don't leak into the next session.
                        state.tempInvitedEmails = [];
                        // Same for a registration setup staged in the link/API panel.
                        state.draftRegistrationApi = null;
                        // Reload active tournament from local storage to clean up any draft state
                        loadFromLocalStorage();
                        updateUIState();
                        renderPlayerList();
                        renderCurrentRound();
                        renderStandings();
                    }
                    // Defer form reset until after the close transition to avoid a flash of defaults
                    const createTourneyForm = document.getElementById('create-tournament-form');
                    setTimeout(() => {
                        if (createTourneyForm && !modal.classList.contains('active')) createTourneyForm.reset();
                    }, 300);
                }
                modal.classList.remove('active');
            } else {
                document.querySelectorAll('.modal-overlay').forEach(m => m.classList.remove('active'));
            }
            return;
        }

        // 2. Click Outside Modal (Overlay Background) to Close & Save
        if (e.target.classList.contains('modal-overlay')) {
            // The Welcome Page editor holds unsaved, non-form work; never close it on a
            // stray background click — it has explicit Close / Save Page buttons.
            if (e.target.id === 'welcome-editor-modal') {
                return;
            }
            // Only treat this as an outside click if the press also STARTED on the
            // overlay. Releasing here after dragging from inside the popup (e.g.
            // selecting text) should not count as clicking outside.
            if (mouseDownTarget !== e.target) {
                return;
            }
            e.preventDefault();
            const modal = e.target;
            
            if (modal.id === 'add-player-modal') {
                const isEditing = !!state.editingPlayerId;
                if (!isEditing) {
                    if (isPlayerFormFilled()) {
                        const confirmCancel = await window.Dialog.confirm(
                            tr('Are you sure you want to cancel player registration? Your changes will be lost.'),
                            tr('Cancel Registration'),
                            tr('Yes, Cancel'),
                            true,
                            tr('Keep Editing')
                        );
                        if (!confirmCancel) {
                            return; // Keep modal open
                        }
                    }
                    state.editingPlayerId = null;
                    modal.classList.remove('active');
                    // Defer form reset until after the close transition to avoid a flash of defaults
                    const addPlayerForm = document.getElementById('add-player-form');
                    setTimeout(() => {
                        if (addPlayerForm && !modal.classList.contains('active')) addPlayerForm.reset();
                    }, 300);
                } else {
                    // Editing a player - existing behavior is to auto-save and close
                    await autoSaveModalInputs(modal);
                    modal.classList.remove('active');
                }
            } else if (modal.id === 'create-tournament-modal') {
                const isEdit = modal.dataset.mode === 'edit';
                if (!isEdit) {
                    if (isTournamentFormEdited()) {
                        const confirmCancel = await window.Dialog.confirm(
                            tr('Are you sure you want to cancel tournament creation? Your changes will be lost.'),
                            tr('Cancel Tournament'),
                            tr('Yes, Cancel'),
                            true,
                            tr('Keep Editing')
                        );
                        if (!confirmCancel) {
                            return; // Keep modal open
                        }
                    }
                    // Creation cancelled: discard any staged cooperation invites so
                    // they are never sent and don't leak into the next session.
                    state.tempInvitedEmails = [];
                    // Same for a registration setup staged in the link/API panel.
                    state.draftRegistrationApi = null;
                    // Reload active tournament from local storage to clean up any draft state
                    loadFromLocalStorage();
                    updateUIState();
                    renderPlayerList();
                    renderCurrentRound();
                    renderStandings();

                    modal.classList.remove('active');
                    // Defer form reset until after the close transition to avoid a flash of defaults
                    const createTourneyForm = document.getElementById('create-tournament-form');
                    setTimeout(() => {
                        if (createTourneyForm && !modal.classList.contains('active')) createTourneyForm.reset();
                    }, 300);
                } else {
                    // Editing a tournament - existing behavior is to auto-save and close
                    await autoSaveModalInputs(modal);
                    modal.classList.remove('active');
                }
            } else {
                // Other modals
                await autoSaveModalInputs(modal);
                modal.classList.remove('active');
            }
            return;
        }

        const item = e.target.closest('.nav-item[data-target], .logo-area[data-target]');
        if (!item) return;

        e.preventDefault();
        if (item.classList.contains('disabled')) return;

        const targetId = item.getAttribute('data-target');

        // The LEFT sidebar always refers to the arbiter's OWN context. While spectating,
        // clicking ANY enabled sidebar tab exits watch mode and restores their own
        // tournament — the watched tournament is navigated via the top panel only.
        // (Tournament tabs are disabled/grayed when they have no own tournament, so
        // those clicks are blocked by the .disabled guard above.)
        if (state.isSpectatorMode) {
            state.isSpectatorMode = false;
            state.activeWelcomePage = null;
            loadFromLocalStorage(); // Restore user's own data
            // Re-point the arbiter-side subscriptions at the tournament that is
            // open again — the same thing exitWatchMode() does, and for the same
            // reason: they sat through the watch declining to act, and a fresh
            // subscription redelivers whatever arrived meanwhile.
            if (window.app && typeof window.app.setupActiveTournamentSync === 'function') {
                window.app.setupActiveTournamentSync();
            }
            updateUIState();
            renderPlayerList();
            renderCurrentRound();
            renderStandings();
        }

        if (targetId === 'pairings') {
            if (state.isSpectatorMode) {
                state.viewedRoundIndex = Math.max(0, state.rounds.length - 1);
            }
            renderCurrentRound();
        }

        showView(targetId);
        updateSpectatorUI(); // Sync UI mode

        if (targetId === 'standings') renderStandings();
        if (targetId === 'tournaments') renderTournamentsPage();
        if (targetId === 'databases') renderDatabasesPage();
        if (targetId === 'organizers') renderOrganizersPage();
        if (['ongoing', 'upcoming', 'finished'].includes(targetId)) {
            localStorage.setItem('lastPublicView', targetId);
            renderPublicTournaments(targetId);
        }

        // Auto-close sidebar on mobile after clicking navigation item
        const sidebar = document.querySelector('.sidebar');
        const backdrop = document.getElementById('sidebar-backdrop');
        if (sidebar && sidebar.classList.contains('active')) {
            sidebar.classList.remove('active');
            if (backdrop) backdrop.classList.remove('active');
        }
    });

    // Enter-to-submit: pressing Enter inside an open popup confirms it, just like
    // clicking its primary button (login, add player, confirm dialogs, etc.).
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Enter' || e.shiftKey || e.ctrlKey || e.metaKey || e.altKey || e.isComposing) return;

        const target = e.target;
        const tag = target.tagName;
        // Multi-line input and rich text keep Enter for themselves wherever they
        // are — it types a newline there, and no popup outranks that.
        if (tag === 'TEXTAREA' || target.isContentEditable) return;

        // Act on the top-most open popup (highest z-index), so a confirm dialog
        // stacked over another modal is the one that gets confirmed.
        const activeModals = Array.from(document.querySelectorAll('.modal-overlay.active'));
        if (activeModals.length === 0) return;
        const modal = activeModals.reduce((top, m) => {
            const z = parseInt(getComputedStyle(m).zIndex, 10) || 0;
            const topZ = parseInt(getComputedStyle(top).zIndex, 10) || 0;
            return z >= topZ ? m : top;
        });

        // While a popup is open it owns the keyboard, so Enter must never reach a
        // control BEHIND it. Focus stays on whatever was clicked to open the popup,
        // and a focused button is natively re-activated by Enter — which is how
        // pressing Enter to save Register Results re-clicked "Register Results" and
        // reloaded the modal from the stored round, silently discarding every result
        // the arbiter had just entered. Suppressing the default here is what stops
        // that; the popup's own commit action is chosen below.
        const focusInsideModal = modal.contains(target);
        if (!focusInsideModal) e.preventDefault();

        // A button or link INSIDE the popup keeps its native Enter: that is exactly
        // what a keyboard user who tabbed to "Cancel" is asking for. (A <select>
        // has no native Enter action of its own, so it falls through and commits
        // the popup — entering results by dropdown and pressing Enter now saves.)
        if ((tag === 'BUTTON' || tag === 'A') && focusInsideModal) return;

        // If the focused field belongs to an input-group with its own action button
        // (e.g. "Invite arbiter by email"), Enter triggers that button instead of
        // submitting the whole form.
        if (tag === 'INPUT' && modal.contains(target) && target.parentElement) {
            const localBtn = target.parentElement.querySelector(
                'button[type="button"]:not(.btn-close-modal):not(.btn-icon):not(.close-modal)'
            );
            if (localBtn && localBtn !== target && !localBtn.disabled) {
                e.preventDefault();
                localBtn.click();
                return;
            }
        }

        // A real <form> submits (runs validation); otherwise click the primary button.
        const form = modal.querySelector('form');
        if (form) {
            e.preventDefault();
            if (typeof form.requestSubmit === 'function') {
                form.requestSubmit();
            } else {
                form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
            }
            return;
        }

        // The footer's primary button FIRST, and only then the first one anywhere
        // in the modal. Document order is a bad proxy for "what Enter means here":
        // Register Results puts six .btn-primary quick-result buttons at the top of
        // its body, so Enter used to stamp 1-0 on the selected board and jump to the
        // next one — pressing it a few times overwrote the results the arbiter had
        // just typed, when all they wanted was to save. The footer is where a modal's
        // commit action lives, so that is where its Enter action comes from.
        const primaryBtn = modal.querySelector('.modal-footer button.btn-primary:not([disabled])')
            || modal.querySelector('button.btn-primary:not([disabled])');
        if (primaryBtn) {
            e.preventDefault();
            primaryBtn.click();
        }
    });

    // Watch Filter Buttons — debounced so typing narrows the list once the user
    // pauses instead of re-rendering (and re-fetching) on every keystroke.
    const searchDebounce = {};
    document.querySelectorAll('.watch-search-input').forEach(input => {
        input.addEventListener('input', (e) => {
            const category = e.target.getAttribute('data-category');
            clearTimeout(searchDebounce[category]);
            searchDebounce[category] = setTimeout(() => {
                renderPublicTournaments(category);
            }, 200);
        });
    });

    // Public tab Sort & Filter panels (Ongoing / Upcoming / Finished).
    initPublicFilters();

    // Mobile Tab Navigation for Spectators
    document.querySelectorAll('.mobile-tab-item').forEach(tab => {
        tab.addEventListener('click', (e) => {
            e.preventDefault();
            const targetId = tab.getAttribute('data-target');
            showView(targetId);
            
            // Sync active highlights across the spectator top tab bar
            document.querySelectorAll('.mobile-tab-item').forEach(item => {
                item.classList.toggle('active', item === tab);
            });

            if (targetId === 'pairings' && state.isSpectatorMode) {
                state.viewedRoundIndex = Math.max(0, state.rounds.length - 1);
                renderCurrentRound();
            }
            if (targetId === 'standings') renderStandings();
        });
    });

    // HTML5 Browser Back/Forward navigation listener
    window.addEventListener('popstate', async (event) => {
        // `event.state` is null for any entry this app did not write — the record
        // the BROWSER created for the page load itself. Landing on a shared link
        // and then moving around inside the app leaves exactly that entry at the
        // bottom of the stack, so without the fallback below, pressing Back onto
        // it changed the address bar and nothing else. The URL says everything
        // needed, so read it.
        let stateData = event.state;
        if (!stateData || !stateData.viewId) {
            const link = parseDeepLink(window.location);
            if (link.tournament) {
                stateData = {
                    viewId: link.tab || 'dashboard',
                    isSpectatorMode: true,
                    spectatorTournamentId: link.tournament
                };
            } else {
                const view = parseViewHash(window.location);
                if (view) stateData = { viewId: view, isSpectatorMode: false, spectatorTournamentId: null };
            }
        }
        if (stateData && stateData.viewId) {
            // Handle spectator mode restoration
            if (stateData.isSpectatorMode && stateData.spectatorTournamentId) {
                if (!state.isSpectatorMode || !state.activeTournament || String(state.activeTournament.id) !== String(stateData.spectatorTournamentId)) {
                    if (window.app && typeof window.app.watchTournament === 'function') {
                        // The tab goes in with it. watchTournament() shows a view
                        // of its own when it finishes, and without this it would
                        // show the welcome page and then be corrected by the
                        // showView() below — a visible flash of the wrong tab on
                        // every Back into a watched tournament.
                        await window.app.watchTournament(stateData.spectatorTournamentId, { view: stateData.viewId });
                    }
                }
            } else if (state.isSpectatorMode) {
                // Exit spectator mode
                state.isSpectatorMode = false;
                state.activeTournament = null;
                state.players = [];
                state.rounds = [];
                loadFromLocalStorage();
                updateUIState();
                updateSpectatorUI();
            }
            showView(stateData.viewId, false); // false = do not pushState again!
        }
    });
}

// ---------------------------------------------------------------------------
// Public tab Sort & Filter (Ongoing / Upcoming / Finished).
// Per-category selections are kept in memory (no persistence — each tab keeps
// its own independent state for the session). Order/filter logic lives in the
// pure helpers in js/utils/tournament-sort.js; this file only wires the DOM.
// ---------------------------------------------------------------------------
const PUBLIC_CATEGORIES = ['ongoing', 'upcoming', 'finished'];

function createPublicFilterDefaults() {
    // dateMode flips the panel between the single "On date" picker and the
    // from–to "Period" pair — only one is visible (and active) at a time.
    return { order: 'default', dateMode: 'date', date: '', from: '', to: '', format: 'all', type: 'all', city: 'all', organizer: 'all' };
}

const publicFilters = {
    ongoing: createPublicFilterDefaults(),
    upcoming: createPublicFilterDefaults(),
    finished: createPublicFilterDefaults()
};

// Human-readable label for a format value (e.g. 'double-round-robin' -> 'Double
// Round Robin'). Mirrors the helper in js/app.js (kept local to avoid importing
// non-exported internals).
function publicFormatLabel(format) {
    return tr(String(format).split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' '));
}

// Rebuilds a category's Format <select> options from the format values present
// in `list` (top-level + per-group), preserving the current selection (falling
// back to 'all' if the format is gone). Skipped when the option set is
// unchanged, so an open dropdown isn't rebuilt out from under the user.
function populatePublicFormatOptions(category, list) {
    const sel = document.getElementById(`${category}-filter-format`);
    if (!sel) return;

    const formats = new Set();
    list.forEach(t => {
        if (t.format) formats.add(t.format);
        if (Array.isArray(t.groups)) {
            t.groups.forEach(g => { if (g && g.format) formats.add(g.format); });
        }
    });
    const sorted = Array.from(formats).sort();
    const desired = ['all', ...sorted];
    const existing = Array.from(sel.options).map(o => o.value);
    const unchanged = existing.length === desired.length && existing.every((v, i) => v === desired[i]);

    if (!unchanged) {
        sel.innerHTML = `<option value="all">${tr('All formats')}</option>` +
            sorted.map(f => `<option value="${escapeHtml(f)}">${escapeHtml(publicFormatLabel(f))}</option>`).join('');
    }
    if (!desired.includes(publicFilters[category].format)) {
        publicFilters[category].format = 'all';
    }
    sel.value = publicFilters[category].format;
}

// Rebuilds a category's City <select> from the distinct cities extracted from
// `list` (case-insensitive de-dup, locale-sorted), preserving the current
// selection if still present. Skipped when the option set is unchanged.
function populatePublicCityOptions(category, list) {
    const sel = document.getElementById(`${category}-filter-city`);
    if (!sel) return;

    const seen = new Map(); // lowercase -> first-seen display form
    list.forEach(t => {
        const city = extractCity(t.location);
        if (city && !seen.has(city.toLowerCase())) seen.set(city.toLowerCase(), city);
    });
    const cities = Array.from(seen.values()).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
    const desired = ['all', ...cities];
    const existing = Array.from(sel.options).map(o => o.value);
    const unchanged = existing.length === desired.length && existing.every((v, i) => v === desired[i]);

    if (!unchanged) {
        sel.innerHTML = `<option value="all">${tr('All cities')}</option>` +
            cities.map(c => `<option value="${escapeHtml(c)}">${escapeHtml(c)}</option>`).join('');
    }
    const current = publicFilters[category].city;
    if (current !== 'all' && !cities.some(c => c.toLowerCase() === String(current).toLowerCase())) {
        publicFilters[category].city = 'all';
    }
    sel.value = publicFilters[category].city;
}

// Rebuilds a category's Organizer <select> from the organizers the tournaments
// in `list` name (by the copy of the name each tournament carries), preserving
// the current selection if still present. Skipped when the option set is
// unchanged. Hidden while no tournament in the tab names an organizer at all.
function populatePublicOrganizerOptions(category, list) {
    const sel = document.getElementById(`${category}-filter-organizer`);
    if (!sel) return;

    const options = publicOrganizerOptions(list);
    const desired = ['all', ...options.map(o => o.value)];
    const existing = Array.from(sel.options).map(o => o.value);
    const unchanged = existing.length === desired.length && existing.every((v, i) => v === desired[i]);

    if (!unchanged) {
        sel.innerHTML = `<option value="all">${tr('All organizers')}</option>` +
            options.map(o => `<option value="${escapeHtml(o.value)}">${escapeHtml(o.label)}</option>`).join('');
    }
    if (!desired.includes(publicFilters[category].organizer)) {
        publicFilters[category].organizer = 'all';
    }
    sel.value = publicFilters[category].organizer;
    const wrap = sel.parentElement;
    if (wrap) wrap.style.display = options.length > 0 ? 'flex' : 'none';
}

// Reflects a category's dateMode onto its panel: highlights the matching
// "On date"/"Period" flip tab and shows only that mode's input(s).
function applyPublicDateModeUI(category) {
    const mode = publicFilters[category].dateMode;
    document.querySelectorAll(`#${category}-filters .date-mode-tab`).forEach(btn => {
        btn.classList.toggle('active', btn.dataset.mode === mode);
    });
    const dateInput = document.getElementById(`${category}-filter-date`);
    const periodWrap = document.getElementById(`${category}-filter-period-wrap`);
    if (dateInput) dateInput.style.display = mode === 'date' ? '' : 'none';
    if (periodWrap) periodWrap.style.display = mode === 'period' ? 'flex' : 'none';
}

// Switches a category between the "On date" and "Period" modes, clearing the
// now-hidden mode's value(s) so a hidden filter can never silently constrain
// the list. Returns true if a non-empty value was cleared (caller re-renders).
function setPublicDateMode(category, mode) {
    const f = publicFilters[category];
    f.dateMode = mode;
    let cleared = false;
    const clear = (id, key) => {
        if (f[key]) { f[key] = ''; cleared = true; }
        const el = document.getElementById(id);
        if (el) el.value = '';
    };
    if (mode === 'date') {
        clear(`${category}-filter-from`, 'from');
        clear(`${category}-filter-to`, 'to');
    } else {
        clear(`${category}-filter-date`, 'date');
    }
    applyPublicDateModeUI(category);
    return cleared;
}

// Pushes a category's in-memory selections back onto its panel controls (used
// after Clear).
function syncPublicFilterControls(category) {
    const f = publicFilters[category];
    const set = (id, val) => { const el = document.getElementById(id); if (el) el.value = val; };
    set(`${category}-sort-order`, f.order);
    set(`${category}-filter-date`, f.date);
    set(`${category}-filter-from`, f.from);
    set(`${category}-filter-to`, f.to);
    set(`${category}-filter-format`, f.format);
    set(`${category}-filter-type`, f.type);
    set(`${category}-filter-city`, f.city);
    set(`${category}-filter-organizer`, f.organizer);
    applyPublicDateModeUI(category);
}

// Wires the toggle button + all controls for the three public Sort & Filter
// panels. Called once from initNavigation().
function initPublicFilters() {
    PUBLIC_CATEGORIES.forEach(category => {
        const toggleBtn = document.getElementById(`btn-toggle-${category}-filters`);
        const panel = document.getElementById(`${category}-filters`);
        if (toggleBtn && panel) {
            toggleBtn.addEventListener('click', () => {
                const isOpen = panel.style.display === 'flex';
                panel.style.display = isOpen ? 'none' : 'flex';
                toggleBtn.classList.toggle('active', !isOpen);
            });
        }

        const orderSel = document.getElementById(`${category}-sort-order`);
        if (orderSel) {
            orderSel.addEventListener('change', () => {
                publicFilters[category].order = orderSel.value;
                renderPublicTournaments(category);
            });
        }

        // "On date" / "Period" flip — only one mode's input(s) are visible at a
        // time; switching clears the hidden mode's values (mutual exclusion).
        document.querySelectorAll(`#${category}-filters .date-mode-tab`).forEach(btn => {
            btn.addEventListener('click', () => {
                const mode = btn.dataset.mode || 'date';
                if (publicFilters[category].dateMode === mode) return;
                const clearedActiveValue = setPublicDateMode(category, mode);
                // Only re-render if the flip removed a value that was filtering.
                if (clearedActiveValue) renderPublicTournaments(category);
            });
        });

        const dateInput = document.getElementById(`${category}-filter-date`);
        const fromInput = document.getElementById(`${category}-filter-from`);
        const toInput = document.getElementById(`${category}-filter-to`);

        if (dateInput) {
            dateInput.addEventListener('change', () => {
                publicFilters[category].date = dateInput.value;
                renderPublicTournaments(category);
            });
        }
        const onPeriodChange = () => {
            publicFilters[category].from = fromInput ? fromInput.value : '';
            publicFilters[category].to = toInput ? toInput.value : '';
            renderPublicTournaments(category);
        };
        if (fromInput) fromInput.addEventListener('change', onPeriodChange);
        if (toInput) toInput.addEventListener('change', onPeriodChange);

        const formatSel = document.getElementById(`${category}-filter-format`);
        if (formatSel) {
            formatSel.addEventListener('change', () => {
                publicFilters[category].format = formatSel.value;
                renderPublicTournaments(category);
            });
        }
        const typeSel = document.getElementById(`${category}-filter-type`);
        if (typeSel) {
            typeSel.addEventListener('change', () => {
                publicFilters[category].type = typeSel.value;
                renderPublicTournaments(category);
            });
        }
        const citySel = document.getElementById(`${category}-filter-city`);
        if (citySel) {
            citySel.addEventListener('change', () => {
                publicFilters[category].city = citySel.value;
                renderPublicTournaments(category);
            });
        }

        const organizerSel = document.getElementById(`${category}-filter-organizer`);
        if (organizerSel) {
            organizerSel.addEventListener('change', () => {
                publicFilters[category].organizer = organizerSel.value;
                renderPublicTournaments(category);
            });
        }

        const clearBtn = document.getElementById(`${category}-filter-clear-btn`);
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                publicFilters[category] = createPublicFilterDefaults();
                syncPublicFilterControls(category);
                renderPublicTournaments(category);
            });
        }
    });
}

// True if any date/format/type/city filter is active for a category (order is
// not a filter — it never removes results). Used to pick the empty-state copy.
function hasActivePublicFilters(category) {
    const f = publicFilters[category];
    return !!(f.date || f.from || f.to ||
        (f.format && f.format !== 'all') ||
        (f.type && f.type !== 'all') ||
        (f.city && f.city !== 'all') ||
        (f.organizer && f.organizer !== 'all'));
}

// Shimmer placeholder cards shown while public tournaments load, so the grid
// fills in seamlessly instead of flashing a "Loading..." box.
function skeletonGridHTML(count = 6) {
    const card = `
        <div class="stat-card tourney-card skeleton-card" aria-hidden="true">
            <div class="skeleton-card-top">
                <span class="skeleton skeleton-avatar"></span>
                <span class="skeleton skeleton-badge"></span>
            </div>
            <span class="skeleton skeleton-line skeleton-title"></span>
            <span class="skeleton skeleton-line skeleton-sub"></span>
            <span class="skeleton skeleton-line skeleton-desc"></span>
            <span class="skeleton skeleton-line skeleton-desc skeleton-desc-short"></span>
        </div>`;
    return card.repeat(count);
}

// The public-tournament fetch returns every public tournament and does the
// name-search client-side, so one fetch serves all three tabs and every search
// term. Cache it briefly: typing then only re-filters in memory instead of
// hitting Firestore per keystroke. Kept short so newly published tournaments
// show up quickly.
const PUBLIC_CACHE_TTL = 10000;
let publicCache = null; // { at: <ms>, promise: Promise<Array> }

function fetchPublicTournaments() {
    const now = Date.now();
    if (!publicCache || now - publicCache.at > PUBLIC_CACHE_TTL) {
        publicCache = { at: now, promise: window.authApp.searchPublicTournaments('') };
        // A failed fetch shouldn't be cached as the answer for the next minute.
        publicCache.promise.catch(() => { publicCache = null; });
    }
    return publicCache.promise;
}

// Drops the cached list so the next render re-queries (used when the signed-in
// user changes, which can change what's visible).
export function invalidatePublicTournamentsCache() {
    publicCache = null;
}

// Guards against out-of-order renders: only the newest run per category may
// write to the grid.
const publicRenderToken = {};

export async function renderPublicTournaments(category) {
    const grid = document.getElementById(`${category}-grid`);
    if (!grid) return;

    const searchInput = document.querySelector(`.watch-search-input[data-category="${category}"]`);
    const searchTerm = searchInput ? searchInput.value : '';

    const token = (publicRenderToken[category] || 0) + 1;
    publicRenderToken[category] = token;
    const isStale = () => publicRenderToken[category] !== token;

    // Only show the loading shimmer when there is nothing to look at yet —
    // re-filtering an already-rendered grid must not blank it out.
    if (!grid.querySelector('.tourney-card:not(.skeleton-card)')) {
        grid.innerHTML = skeletonGridHTML();
    }

    // If auth is not ready yet, keep the loading spinner visible.
    // The Firebase Auth listener will trigger renderPublicTournaments() automatically once ready.
    if (!window.authApp || typeof window.authApp.searchPublicTournaments !== 'function') {
        grid.innerHTML = `<div class="empty-state-container"><div class="empty-state-content glass"><h3>${tr('Database Connection Error')}</h3></div></div>`;
        return;
    }

    if (typeof window.authApp.isInitialized === 'function' && !window.authApp.isInitialized()) {
        return;
    }

    try {
        const fetched = await fetchPublicTournaments();
        if (isStale()) return;

        const term = searchTerm.trim().toLowerCase();
        const allPublic = term
            ? fetched.filter(t => t.name && t.name.toLowerCase().includes(term))
            : fetched;
        // The current result set for this tab (after name-search, but before the
        // panel's date/format/type/city filters). Dropdowns come from this set.
        const categoryList = allPublic.filter(t => t.status === category);

        populatePublicFormatOptions(category, categoryList);
        populatePublicCityOptions(category, categoryList);
        populatePublicOrganizerOptions(category, categoryList);

        const f = publicFilters[category];
        // On the Ongoing tab, a started tournament with no endDate is classified
        // as still running (see the status computation in firebase-auth.js), so
        // the date filters must treat its range as open-ended — otherwise a
        // tournament the app itself calls "ongoing today" wouldn't match today.
        const openEnded = category === 'ongoing';
        const filtered = sortPublicTournaments(
            categoryList.filter(t =>
                matchesFormat(t, f.format) &&
                matchesRatingType(t, f.type) &&
                matchesCity(t, f.city) &&
                matchesOrganizer(t, f.organizer) &&
                matchesSpecificDate(t, f.date, openEnded) &&
                matchesPeriod(t, f.from, f.to, openEnded)
            ),
            category,
            f.order
        );

        if (filtered.length === 0) {
            // Offer to browse the two other categories (matches the empty-state design).
            const categoryLabels = { ongoing: 'ongoing', upcoming: 'upcoming', finished: 'finished' };
            const otherCategories = ['ongoing', 'upcoming', 'finished'].filter(c => c !== category);
            const browseButtons = otherCategories.map((c, i) => `
                <button class="${i === 0 ? 'btn-primary' : 'btn-outline'} btn-large" data-show-view="${c}">
                    <i class="ri-search-eye-line"></i> ${tr('Browse {cat} tournaments', { cat: tr(categoryLabels[c]) })}
                </button>
            `).join('');

            // Distinguish "filters too narrow" (there ARE tournaments in this
            // tab, the panel filters just hid them all) from the plain
            // no-results case (nothing matched the search / empty tab).
            const filteredOut = categoryList.length > 0 && hasActivePublicFilters(category);
            const title = filteredOut
                ? tr('No {cat} tournaments match your filters', { cat: tr(category) })
                : tr('No {cat} tournaments found', { cat: tr(category) });
            const desc = filteredOut
                ? tr('Your filters may be too narrow. Try clearing or widening them, or check another category.')
                : tr('Try a different search term or check another category.');

            grid.innerHTML = `
                <div class="tournaments-empty">
                    <img class="tournaments-empty-art" src="/assets/illustrations/no-tournaments.jpg"
                        alt="Two people playing chess" loading="lazy">
                    <h2 class="tournaments-empty-title">${title}</h2>
                    <p class="tournaments-empty-desc">${desc}</p>
                    <div class="tournaments-empty-actions">
                        ${browseButtons}
                    </div>
                </div>
            `;
            return;
        }

        grid.innerHTML = filtered.map(t => {
            const statusLabel = t.status.charAt(0).toUpperCase() + t.status.slice(1);
            const statusClass = t.status === 'ongoing' ? 'active' : t.status === 'finished' ? 'completed' : 'muted';
            const dateRange = t.startDate ? (t.endDate ? `${t.startDate} ${tr('to')} ${t.endDate}` : t.startDate) : tr('No date');
            const timeInfo = t.startTime ? ` • ${t.startTime}` : '';
            return `
            <div class="stat-card tourney-card" style="flex-direction: column; align-items: flex-start; gap: 0.5rem; cursor: pointer;" data-watch-tournament="${escapeHtml(String(t.id))}">
                <div style="display: flex; width: 100%; justify-content: space-between; align-items: center;">
                    <div class="stat-icon blue" style="width: 40px; height: 40px; font-size: 1.25rem;"><i class="ri-eye-line"></i></div>
                    <span class="status-badge ${statusClass}">${escapeHtml(tr(statusLabel))}</span>
                </div>
                <div style="margin-top: 0.5rem;">
                    <h3 style="color: var(--text-main); font-size: 1.1rem; margin-bottom: 0.25rem;">${escapeHtml(t.name)}</h3>
                    <p class="user-role">${tr(t.format === 'swiss' ? 'Swiss' : t.format === 'monrad' ? 'Monrad' : t.format === 'konrad' ? 'Konrad' : t.format === 'arena' ? 'Arena' : t.format === 'round-robin' ? 'Round Robin' : t.format === 'team' ? 'Team' : 'Double Round')} • ${escapeHtml(dateRange)}${escapeHtml(timeInfo)}</p>
                    ${t.location ? `<p class="user-role" style="color: var(--primary); font-size: 0.75rem;"><i class="ri-map-pin-line"></i> ${escapeHtml(t.location)}</p>` : ''}
                    ${t.organizerId && t.organizerName ? `<p class="tourney-card-organizer"><i class="ri-community-line"></i> ${escapeHtml(t.organizerName)}</p>` : ''}
                </div>
                <div style="margin-top: 0.5rem; font-size: 0.85rem; color: var(--text-muted); display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;">
                    ${escapeHtml(t.welcomeMessage || tr('View live pairings and standings for this tournament.'))}
                </div>
            </div>
            `;
        }).join('');
    } catch (error) {
        console.error('Public page error:', error);
        grid.innerHTML = `<div class="empty-state-container"><div class="empty-state-content glass"><h3>${tr('Error')}</h3><p>${escapeHtml(error.message)}</p></div></div>`;
    }
}

export function updateSpectatorUI() {
    // Toggle body class for CSS-based visibility rules
    document.body.classList.toggle('spectator-mode', state.isSpectatorMode);

    syncLiveTabVisibility();

    const adminElements = [
        btnCreateTourneyMain, btnOpenTourneyMain, btnAddPlayer,
        btnGenerateRound, btnRegisterResults, btnUndoRound,
        btnEditTournament, btnAssignSNR, btnToggleAllStatus,
        btnCloseTournament, btnCreateTourneyPage
    ];

    adminElements.forEach(el => {
        if (el) el.style.display = state.isSpectatorMode ? 'none' : '';
    });

    // The left sidebar always shows the arbiter/public nav. In spectator mode the
    // CSS (body.spectator-mode) hides the arbiter-only items, leaving the public
    // lists (Ongoing/Upcoming/Finished) + Settings — clicking one exits the watch.
    if (arbiterNav) {
        arbiterNav.style.display = 'flex';
    }

    // Hide edit/delete buttons in player list
    const playerTable = document.getElementById('players-table');
    if (playerTable) playerTable.classList.toggle('spectator-view', state.isSpectatorMode);

    // Collapse the spectator filter panels (players + standings) when leaving watch mode,
    // so an arbiter never sees a stray open filter panel in their own views.
    if (!state.isSpectatorMode) {
        ['spectator-player-filters', 'standings-filters'].forEach(id => {
            const panel = document.getElementById(id);
            if (panel) panel.style.display = 'none';
        });
        ['btn-toggle-player-filters', 'btn-toggle-standings-filters'].forEach(id => {
            const btn = document.getElementById(id);
            if (btn) btn.classList.remove('active');
        });
    }

    // Role changes hide/show parts of the hoisted header (arbiter-only titles,
    // spectator-only badges), so the top panel has to be re-evaluated.
    syncTopHeader();
}

// Put the newly shown view at its top. Every scroller is reset because which one
// carries the page depends on the viewport and the view: the desktop layout scrolls
// .view-container, under 768px the CSS hands scrolling back to the document, and on
// a wide screen Players, Pairings and Standings scroll inside the view — each card's
// body, and the view itself when even that does not fit (css/main.css).
function scrollViewToTop(viewId) {
    const container = document.querySelector('.view-container');
    if (container) container.scrollTop = 0;
    if (window.scrollY) window.scrollTo(0, 0);
    const view = document.getElementById(viewId);
    if (view) [view, ...view.querySelectorAll('.card-body')].forEach(box => { box.scrollTop = 0; });
}

// `replace`: record the view in place of the current history entry instead of on
// top of it — for a view the address already pointed at (a shared link opening),
// where a pushed entry would make Back return to the same page.
export function showView(viewId, pushToHistory = true, { replace = false } = {}) {
    if (!viewId) return;

    // Most views share one scroll box (.view-container on desktop, the document
    // itself on mobile), so the scroll position left behind by the tab you are
    // leaving is still there when the next one is shown — open a long database and
    // then the Players tab, and Players comes up already scrolled past its header.
    // Remembered here so the reset below only fires on an actual tab change; the
    // many re-renders that call showView with the view already open must not yank
    // the page back to the top under the arbiter's hands.
    const previousViewId = Array.from(views).find(v => v.classList.contains('active'))?.id;

    views.forEach(view => {
        view.classList.remove('active');
        if (view.id === viewId) view.classList.add('active');
    });

    if (previousViewId !== viewId) scrollViewToTop(viewId);

    // Opening the Players page checks NSF memberships afresh, for the groups that require
    // them (js/services/nsf-membership.js decides whether there is anything to check).
    // Only on an actual change of tab: re-renders call this with the page already open.
    if (viewId === 'players' && previousViewId !== viewId) refreshNsfCheck({ force: true });

    // Sync navigation highlight. While spectating, NO left sidebar tab is highlighted
    // (the sidebar refers to the arbiter's own context, not the watched tournament —
    // the active tab is shown on the top panel instead).
    if (allNavItems) {
        allNavItems.forEach(nav => {
            nav.classList.remove('active');
            // Highlight matching sidebar item, but exclude the logo area
            if (!state.isSpectatorMode && nav.getAttribute('data-target') === viewId && !nav.classList.contains('logo-area')) {
                nav.classList.add('active');
            }
        });
    }
    syncNavGroup(state.isSpectatorMode ? null : viewId, previousViewId !== viewId);

    // Lift the newly shown view's title into the top panel
    syncTopHeader();

    // The group selector belongs to the roster/pairings/standings views only, so
    // it has to be re-evaluated on every view change and not just when the
    // tournament data changes (see GROUP_BAR_VIEWS in js/app.js).
    renderDashboardGroupsBar();

    // Persist view
    localStorage.setItem('currentView', viewId);

    // Push state to browser history for backward/forward navigation
    if (pushToHistory) {
        try {
            const watchedId = state.isSpectatorMode && state.activeTournament
                ? state.activeTournament.id
                : null;
            const stateObj = {
                viewId: viewId,
                isSpectatorMode: state.isSpectatorMode,
                spectatorTournamentId: watchedId
            };

            // WHILE WATCHING, THE ADDRESS BAR NAMES THE TOURNAMENT AND THE TAB.
            // That is the whole point of the change: '#standings' addressed a tab
            // of whatever tournament the reader's own browser had open, so it
            // meant something different to every person it was pasted to.
            // '/t/<id>/standings' means one thing everywhere.
            //
            // Only the five tabs a watched tournament actually has get the
            // tournament path. A spectator who opens Settings from the sidebar is
            // no longer looking at the tournament, and a URL that said otherwise
            // would be a link to a page that does not exist; the tournament is
            // still remembered in `stateObj`, so Back returns to it.
            //
            // Root-relative ('/#settings', not '#settings') because a bare
            // fragment resolves against the CURRENT path: leaving a watch from
            // '/t/x/standings' would otherwise produce '/t/x/standings#ongoing',
            // which reloads straight back into the tournament that was just left.
            const url = (watchedId && TOURNAMENT_VIEWS.includes(viewId))
                ? buildTournamentPath(watchedId, viewId)
                : `/#${viewId}`;

            // Re-renders call showView() with the view already open — many times
            // during a round — and pushing an identical URL each time buries the
            // previous page under a stack of duplicates, so Back appears dead.
            // Same address, same history entry.
            const target = new URL(url, location.href).href;
            if (replace || target === location.href) history.replaceState(stateObj, '', target);
            else history.pushState(stateObj, '', target);
        } catch (e) {
            console.error('History pushState error:', e);
        }
    }

    // Sync active highlights in spectator mobile tabs if they exist — for screen
    // readers too, which cannot see the colour that marks the open tab.
    document.querySelectorAll('.mobile-tab-item').forEach(tab => {
        const isOpen = tab.getAttribute('data-target') === viewId;
        tab.classList.toggle('active', isOpen);
        if (isOpen) tab.setAttribute('aria-current', 'page');
        else tab.removeAttribute('aria-current');
    });

    // The guide is static content and needs no signed-in state to draw. It is also
    // ~100 KB of prose in two languages that most visits never open, so it is
    // fetched the first time the view is shown rather than with every page load.
    // (Offline, it is there once it has been opened online: the service worker
    // caches what is used.)
    if (viewId === 'guide') {
        import('./guide.js')
            .then(m => m.renderGuide())
            .catch(err => console.error('Could not load the User Guide:', err));
    }

    // Automatically trigger render for dynamic views when they are shown
    if (window.authApp && typeof window.authApp.isInitialized === 'function' && window.authApp.isInitialized()) {
        if (viewId === 'tournaments') {
            // Always open the tab with folders collapsed.
            if (typeof collapseAllFolders === 'function') collapseAllFolders();
            if (typeof renderTournamentsPage === 'function') renderTournamentsPage();
        } else if (viewId === 'myplay') {
            renderMyPlayTournaments();
        } else if (viewId === 'databases') {
            if (typeof renderDatabasesPage === 'function') renderDatabasesPage();
        } else if (viewId === 'organizers') {
            renderOrganizersPage();
        } else if (['ongoing', 'upcoming', 'finished'].includes(viewId)) {
            if (typeof renderPublicTournaments === 'function') renderPublicTournaments(viewId);
        }
    }
}

export function updateNavigationVisibility() {
    // If Firebase Auth has not completed its initial state check yet,
    // do not run redirects or navigation toggles to avoid guest-redirection race conditions.
    if (window.authApp && typeof window.authApp.isInitialized === 'function' && !window.authApp.isInitialized()) {
        return;
    }

    const isLoggedIn = window.authApp && window.authApp.isAuthenticated();

    syncLiveTabVisibility();

    let role = 'guest';

    if (isLoggedIn) {
        const profile = window.app && window.app.getUserProfile ? window.app.getUserProfile() : state.userProfile;
        role = profile.role || 'organizer';

        // Migration: treat old 'organizer_player' as 'organizer'
        if (role === 'organizer_player') role = 'organizer';
    }

    const currentView = Array.from(views).find(v => v.classList.contains('active'))?.id;

    // WHILE WATCHING A TOURNAMENT, NOBODY GETS REDIRECTED. The role redirects below
    // describe where an account belongs in its OWN context; a watched tournament is
    // not that context, and its pages legitimately live in views the role is
    // otherwise kept out of (the spectator welcome page IS 'dashboard', and there
    // are 'players'/'pairings'/'standings' tabs on the top panel).
    //
    // Bouncing anyway is what made registering or unregistering look like it merged
    // two pages: those flows call updateUIState() -> here, the view underneath was
    // swapped for My Tournaments, while the tournament header and the spectator tab
    // bar (both outside .view-container, driven by body.spectator-mode) stayed put.
    // Leaving watch mode goes through exitWatchMode(), which picks the view itself.
    // A tournament about to be opened from the address counts as watched already
    // (isAwaitingWatch, js/app.js): redirecting now would flash the landing page
    // and push it onto the history before the tournament arrives.
    const isWatching = !!state.isSpectatorMode
        || !!(window.app && typeof window.app.isAwaitingWatch === 'function' && window.app.isAwaitingWatch());

    if (btnHelpTour) btnHelpTour.style.display = role === 'guest' ? 'none' : 'flex';

    // Toggle navigation items based on role
    if (role === 'guest') {
        if (navDashboard) navDashboard.style.display = 'none';
        if (navMyTournaments) navMyTournaments.style.display = 'none';
        if (navMyPlay) navMyPlay.style.display = 'none';
        if (navDatabases) navDatabases.style.display = 'none';
        if (navOrganizers) navOrganizers.style.display = 'none';
        if (navPlayers) navPlayers.style.display = 'none';
        if (navPairings) navPairings.style.display = 'none';
        if (navStandings) navStandings.style.display = 'none';
        if (navLive) navLive.style.display = 'none';
        if (navOngoing) navOngoing.style.display = 'flex';
        if (navUpcoming) navUpcoming.style.display = 'flex';
        if (navFinished) navFinished.style.display = 'flex';
        if (navSettings) navSettings.style.display = 'flex';
        if (navGuide) navGuide.style.display = 'none';

        // If they are on a restricted view, move them to landing
        if (!isWatching && currentView && !['landing', 'ongoing', 'upcoming', 'finished', 'settings'].includes(currentView)) {
            showView('landing');
        }

        const logoArea = document.querySelector('.logo-area');
        if (logoArea) logoArea.setAttribute('data-target', 'landing');
    } else if (role === 'player') {
        if (navDashboard) navDashboard.style.display = 'none';
        if (navMyTournaments) navMyTournaments.style.display = 'none';
        if (navMyPlay) navMyPlay.style.display = 'flex';
        if (navDatabases) navDatabases.style.display = 'none';
        if (navOrganizers) navOrganizers.style.display = 'none';
        if (navPlayers) navPlayers.style.display = 'none';
        if (navPairings) navPairings.style.display = 'none';
        if (navStandings) navStandings.style.display = 'none';
        if (navLive) navLive.style.display = 'none';
        if (navOngoing) navOngoing.style.display = 'flex';
        if (navUpcoming) navUpcoming.style.display = 'flex';
        if (navFinished) navFinished.style.display = 'flex';
        if (navSettings) navSettings.style.display = 'flex';
        if (navGuide) navGuide.style.display = 'none';

        // 'myplay' is a player's home, the way the dashboard is an arbiter's:
        // it is where they land, and where the logo takes them back to. Note
        // 'tournaments' (the ARBITER tab of the same name) stays restricted —
        // the two are different views despite sharing a label.
        if (!isWatching
            && (!currentView || ['landing', 'dashboard', 'tournaments', 'databases', 'organizers', 'players', 'pairings', 'standings', 'guide'].includes(currentView))) {
            showView('myplay');
        }

        const logoArea = document.querySelector('.logo-area');
        if (logoArea) logoArea.setAttribute('data-target', 'myplay');
    } else if (role === 'organizer') {
        if (navDashboard) navDashboard.style.display = 'flex';
        if (navMyTournaments) navMyTournaments.style.display = 'flex';
        if (navMyPlay) navMyPlay.style.display = 'none';
        if (navDatabases) navDatabases.style.display = 'flex';
        if (navOrganizers) navOrganizers.style.display = 'flex';
        if (navPlayers) navPlayers.style.display = 'flex';
        if (navPairings) navPairings.style.display = 'flex';
        if (navStandings) navStandings.style.display = 'flex';
        // Always there once a tournament is open, broadcasting or not: the Live
        // view carries the arbiter's broadcast panel, which is the only place the
        // broadcast can be switched on (see the comment on broadcastEnabled).
        if (navLive) navLive.style.display = state.activeTournament ? 'flex' : 'none';
        if (navOngoing) navOngoing.style.display = 'flex';
        if (navUpcoming) navUpcoming.style.display = 'flex';
        if (navFinished) navFinished.style.display = 'flex';
        if (navSettings) navSettings.style.display = 'flex';
        if (navGuide) navGuide.style.display = 'flex';

        // Arbiters have access to all views
        if (currentView === 'landing') {
            showView('dashboard');
        }

        const logoArea = document.querySelector('.logo-area');
        if (logoArea) logoArea.setAttribute('data-target', 'dashboard');
    }

    // Show/Hide Profile section based on login status
    const profileSection = document.getElementById('settings-profile-section');
    if (profileSection) {
        profileSection.style.display = (role === 'guest') ? 'none' : 'block';
    }

    // "Delete my profile" needs a profile to delete, so it follows the profile
    // section rather than the role: a guest has no account to remove.
    const deleteProfileRow = document.getElementById('settings-delete-profile-row');
    if (deleteProfileRow) {
        deleteProfileRow.style.display = (role === 'guest') ? 'none' : 'block';
    }

    // Tab hotkeys are arbiter-only (they bind arbiter top-panel buttons).
    const hotkeysSection = document.getElementById('settings-hotkeys-section');
    if (hotkeysSection) {
        hotkeysSection.style.display = (role === 'organizer') ? 'block' : 'none';
    }

    // Ongoing / Upcoming / Finished sit in the Other Tournaments folder for
    // arbiters only; players and guests keep them as plain tabs.
    setNavGroupFolded(role === 'organizer');

    // The sidebar order is the profile's, so it changes with who is signed in.
    applyNavOrder();
    const navOrderSection = document.getElementById('settings-nav-order-section');
    if (navOrderSection) {
        navOrderSection.style.display = (role === 'organizer') ? 'block' : 'none';
    }
    const resetNavOrderBtn = document.getElementById('btn-reset-nav-order');
    if (resetNavOrderBtn) resetNavOrderBtn.disabled = !hasCustomNavOrder();
}

/**
 * Automatically saves input field data of active modals when closing or clicking outside them.
 * @param {HTMLElement} modal - The modal overlay element being closed.
 */
async function autoSaveModalInputs(modal) {
    if (!modal) return;
    
    if (modal.id === 'add-player-modal') {
        const givenName = document.getElementById('player-given-name')?.value.trim();
        const lastName = document.getElementById('player-last-name')?.value.trim();
        const isEditing = !!state.editingPlayerId;
        
        // Save only if editing, or if a new player has at least some name details typed
        if (isEditing || givenName || lastName) {
            const playerData = {
                startNo: document.getElementById('player-start-no')?.value || '',
                class: document.getElementById('player-class')?.value || '',
                title: document.getElementById('player-title')?.value || '',
                givenName: givenName || '',
                lastName: lastName || '',
                club: document.getElementById('player-club')?.value || '',
                fed: document.getElementById('player-fed')?.value || '',
                birthDate: document.getElementById('player-birthdate')?.value || '',
                // A full date wins and the year is derived from it. Derived by
                // reading the ISO string, not via `new Date(...)`: a date-only value
                // parses as UTC midnight, so west of Greenwich a child born on
                // 1 January would land in the previous year's age class.
                birthYear: playerBirthYear({
                    birthDate: document.getElementById('player-birthdate')?.value || ''
                }) || document.getElementById('player-birthyear')?.value || '',
                fideId: document.getElementById('player-fide-id')?.value || '',
                nsfId: document.getElementById('player-nsf-id')?.value || '',
                rating: document.getElementById('player-rating')?.value || '',
                kFactor: document.getElementById('player-k-factor')?.value || '',
                standardRating: document.getElementById('player-std-rating')?.value || '0',
                rapidRating: document.getElementById('player-rapid-rating')?.value || '0',
                blitzRating: document.getElementById('player-blitz-rating')?.value || '0',
                // The same link the Add Player button saves (js/app.js), so a player
                // picked from the database keeps it when the dialog is closed
                // instead of submitted.
                dbPlayerId: document.getElementById('player-db-player-id')?.value || '',
                status: document.getElementById('player-status')?.checked !== false
            };
            
            if (isEditing) {
                const index = state.players.findIndex(p => p.id === state.editingPlayerId);
                if (index !== -1) {
                    state.players[index] = { ...state.players[index], ...playerData };
                }
            } else {
                state.players.push({ id: Date.now().toString(), ...playerData });
            }
            
            // Save state, refresh UI and lists
            saveToLocalStorage();
            renderPlayerList();
            renderCurrentRound();
            renderStandings();
            updateUIState();
        }
        state.editingPlayerId = null;
        
    } else if (modal.id === 'create-tournament-modal') {
        const name = document.getElementById('tourney-name')?.value.trim();
        if (name && state.activeTournament) {
            const createTourneyModal = document.getElementById('create-tournament-modal');
            const isEdit = createTourneyModal?.dataset.mode === 'edit';
            
            if (isEdit) {
                // General (tournament-level) fields — always applied.
                const generalData = {
                    name: name,
                    startDate: document.getElementById('tourney-start')?.value || '',
                    startTime: document.getElementById('tourney-start-time')?.value || '09:00',
                    endDate: document.getElementById('tourney-end')?.value || '',
                    endTime: document.getElementById('tourney-end-time')?.value || '18:00',
                    location: document.getElementById('tourney-location')?.value || '',
                    arbiterName: document.getElementById('tourney-arbiter-name')?.value.trim() || '',
                    arbiterEmail: document.getElementById('tourney-arbiter-email')?.value.trim() || '',
                    arbiterPhone: document.getElementById('tourney-arbiter-phone')?.value.trim() || '',
                    isPublic: document.getElementById('tourney-public')?.checked || false,
                    pairingPrintStyle: document.getElementById('tourney-pairing-print')?.value || '',
                    pairingPublish: document.getElementById('tourney-pairing-publish')?.value === 'manual' ? 'manual' : 'auto',
                    welcomeMessage: document.getElementById('tourney-welcome')?.value || '',
                    registrationOpen: document.getElementById('tourney-reg-open')?.checked || false,
                    registrationDeadline: document.getElementById('tourney-reg-deadline')?.value || '',
                    registrationDeadlineTime: document.getElementById('tourney-reg-deadline-time')?.value || '23:59',
                    requirePhone: document.getElementById('tourney-require-phone')?.checked || false,
                    requireNsfMembership: document.getElementById('tourney-require-nsf')?.checked || false,
                    nsfSeries: document.getElementById('tourney-nsf-series')?.value || '',
                    registrationApproval: document.getElementById('tourney-reg-approval')?.value || 'review',
                    keepRegistrationOpen: document.getElementById('tourney-keep-reg-open')?.checked || false
                };

                // Per-group settings fields — only meaningful for a legacy no-groups
                // tournament (where the top-level fields ARE canonical). For multi-group
                // tournaments these inputs are not the source of truth (per-group tabs
                // are), so writing them here would clobber the active group's settings
                // on the next sync — matching the create-form's `hasGroups` guard.
                const hasGroups = state.activeTournament.groups && state.activeTournament.groups.length > 0;
                const roundsVal = parseInt(document.getElementById('tourney-rounds')?.value) || 7;
                const singleGroupData = {
                    format: document.getElementById('tourney-format')?.value || 'swiss',
                    type: document.getElementById('tourney-type')?.value || 'standard',
                    rounds: roundsVal,
                    roundsCount: roundsVal,
                    seedColor: document.getElementById('tourney-seed-color')?.value || 'white',
                    pointSystem: document.getElementById('tourney-points')?.value || '1-0.5-0'
                };
                const formData = hasGroups ? generalData : { ...generalData, ...singleGroupData };

                const oldType = getActiveSettings().type;
                state.activeTournament = { ...state.activeTournament, ...formData };
                
                // If rating type changed, update player ratings (only for legacy
                // no-groups tournaments — multi-group type is edited per group).
                if (!hasGroups && oldType !== formData.type && state.players?.length > 0) {
                    state.players = state.players.map(player => {
                        const { display } = getPlayerDisplayRating(player);
                        let newRating = display;
                        if (display === '0' && player.rating && player.rating !== '0') {
                            newRating = player.rating;
                        }
                        return { ...player, rating: newRating };
                    });
                }
                
                // Save state, refresh UI and lists
                saveToLocalStorage();
                updateUIState();
                renderPlayerList();
                renderCurrentRound();
                renderStandings();
            }
        }
        
    } else if (modal.id === 'edit-pairings-modal') {
        if (typeof saveEditedPairings === 'function') {
            await saveEditedPairings();
        }
    } else if (modal.id === 'register-results-modal') {
        if (typeof saveRegisterResults === 'function') {
            await saveRegisterResults();
        }
    }
}
